<template>
    <div>
        <div class="table-count">
            <template v-if="busy">{{ $t('common.loading') }}</template>
            <template v-else>{{ $t('company.showing_students', { shown: rows.length, total }) }}</template>
        </div>

        <kimsa-grid-table :headers="headers" :items="rows" :loading="busy" item-key="id" disable-sort
            :skeleton-count="perPage" :no-data-text="$t('common.not_found')">
            <template v-slot:item.name="{ item }">
                <truncated-text :text="item.name" />
            </template>
            <template v-slot:item.adherence="{ item }">
                <span :class="item.adherenceClass">{{ item.adherence }}</span>
            </template>
            <template v-slot:footer>
                <div v-if="pageCount > 1" class="table-footer">
                    <kimsa-pagination height="auto" :pageCount="pageCount" @page="page = $event - 1"
                        :total-visible="isMobile ? 4 : 7" />
                </div>
            </template>
        </kimsa-grid-table>
    </div>
</template>

<script>
import { UserIndex } from "@/utils/Algolia";
import { fetchStudentsAdvances } from "@/utils/Statistics";
import { computeStreak } from "@/utils/Routine";
import KimsaGridTable from "@/components/common/KimsaGridTable";
import KimsaPagination from "@/components/common/KimsaPagination";
import TruncatedText from "@/components/common/TruncatedText";
import { civilDaysBetween } from '@/utils/Common';

const PER_PAGE = 20
const ATTENDANCE_DAYS = 30
const STREAK_DAYS = 84
const RISK_THRESHOLD = 60
const GOOD_THRESHOLD = 80
const CACHE_TTL = 5 * 60 * 1000
const CACHE_MAX = 40
const cache = new Map()

const cacheKey = (coachId, page) => `${coachId}|${page}`

const readCache = (key) => {
    const entry = cache.get(key)
    if (!entry) return null
    if ((Date.now() - entry.at) >= CACHE_TTL) {
        cache.delete(key)
        return null
    }
    return entry
}

const writeCache = (key, entry) => {
    if (cache.size >= CACHE_MAX) cache.clear()
    cache.set(key, { ...entry, at: Date.now() })
}

export default {
    name: "CoachStudentsTable",
    components: { KimsaGridTable, KimsaPagination, TruncatedText },
    props: {
        coachId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            students: [],
            statsByStudent: {},
            total: 0,
            pageCount: 0,
            page: 0,
            perPage: PER_PAGE,
            loading: false,
            loadingStats: false,
        }
    },
    computed: {
        busy(vm = this) {
            return vm.loading || vm.loadingStats
        },
        headers(vm = this) {
            return [
                { text: vm.$t('common.student'), value: 'name', width: 'minmax(140px, 1fr)' },
                { text: vm.$t('company.attendance'), value: 'attendance', width: '110px' },
                { text: vm.$t('company.streak'), value: 'streak', width: '100px' },
                { text: vm.$t('company.last_activity'), value: 'lastActivity', width: '140px' },
                { text: vm.$t('company.stat_adherence'), value: 'adherence', width: '110px' },
            ]
        },
        rows(vm = this) {
            return vm.students.map(student => {
                const stats = vm.statsByStudent[student.id] || null
                return {
                    id: student.id,
                    name: student.name,
                    attendance: stats ? `${stats.completed}/${stats.assigned}` : '—',
                    streak: stats ? vm.streakLabel(stats.streak) : '—',
                    lastActivity: stats ? stats.lastActivity : '—',
                    adherence: stats && stats.adherence !== null ? `${stats.adherence}%` : '—',
                    adherenceClass: vm.adherenceClass(stats),
                }
            })
        },
    },
    mounted() {
        this.loadStudents()
    },
    methods: {
        async loadStudents() {
            const vm = this
            if (!vm.coachId) return

            const key = cacheKey(vm.coachId, vm.page)
            const cached = readCache(key)
            if (cached) {
                vm.students = cached.students
                vm.statsByStudent = cached.statsByStudent
                vm.total = cached.total
                vm.pageCount = cached.pageCount
                vm.loading = false
                vm.loadingStats = false
                return
            }

            vm.loading = true
            vm.loadingStats = true
            vm.statsByStudent = {}
            try {
                const { hits, nbHits, nbPages } = await UserIndex.search('', {
                    filters: `studentSubscription.coachId: ${vm.coachId} AND studentSubscription.paused != 1`,
                    hitsPerPage: vm.perPage,
                    page: vm.page,
                })

                vm.total = nbHits || 0
                vm.pageCount = nbPages || 0
                vm.students = hits.map(hit => ({
                    id: hit.objectID,
                    name: hit.name || hit.email || vm._noAvailableName,
                }))
            } catch (error) {
                vm.loadingStats = false
                vm.toast(vm.$t('common.error_generic'), 'error')
                throw error
            } finally {
                vm.loading = false
            }
            await vm.loadStats()
            writeCache(key, {
                students: vm.students,
                statsByStudent: vm.statsByStudent,
                total: vm.total,
                pageCount: vm.pageCount,
            })
        },
        async loadStats() {
            const vm = this
            const studentIds = vm.students.map(student => student.id)
            if (studentIds.length === 0) {
                vm.loadingStats = false
                return
            }

            const today = vm.$moment().format('YYYY-MM-DD')
            const start = vm.$moment().subtract(STREAK_DAYS - 1, 'days').format('YYYY-MM-DD')
            const attendanceStart = vm.$moment().subtract(ATTENDANCE_DAYS - 1, 'days').format('YYYY-MM-DD')

            try {
                const advances = await fetchStudentsAdvances({
                    coachId: vm.coachId,
                    studentIds,
                    start,
                    end: today,
                })

                const byStudent = {}
                studentIds.forEach(id => { byStudent[id] = [] })
                advances.forEach(item => {
                    if (byStudent[item.studentId]) byStudent[item.studentId].push(item)
                })

                const stats = {}
                studentIds.forEach(id => {
                    stats[id] = vm.buildStudentStats(byStudent[id], attendanceStart, today)
                })
                vm.statsByStudent = stats
            } catch (error) {
                console.log('loadStats error', error)
            } finally {
                vm.loadingStats = false
            }
        },
        buildStudentStats(routines, attendanceStart, today) {
            const vm = this
            const training = routines.filter(routine => routine.type !== 'competence')

            const inWindow = training.filter(routine => (routine.rawDate?.full || '') >= attendanceStart)
            const assigned = inWindow.length
            const completed = inWindow.filter(routine => routine.advance?.post).length

            const ordered = training
                .filter(routine => (routine.routineNumber || 1) === 1)
                .sort((a, b) => (b.rawDate?.full || '').localeCompare(a.rawDate?.full || ''))

            const lastDone = training
                .filter(routine => routine.advance?.post)
                .map(routine => routine.rawDate?.full || '')
                .filter(Boolean)
                .sort()
                .pop()

            return {
                assigned,
                completed,
                adherence: assigned > 0 ? Math.round((completed / assigned) * 100) : null,
                streak: computeStreak(ordered, today),
                lastActivity: vm.lastActivityLabel(lastDone),
            }
        },
        lastActivityLabel(date) {
            const vm = this
            if (!date) return vm.$t('company.no_activity')
            const days = civilDaysBetween(date, vm.$moment().format('YYYY-MM-DD'))
            if (days <= 0) return vm.$t('common.today')
            if (days === 1) return vm.$t('common.yesterday')
            return vm.$tc('dashboard.days_ago', days, { n: days })
        },
        streakLabel(streak) {
            const vm = this
            return vm.$tc('company.streak_days', streak, { n: streak })
        },
        adherenceClass(stats) {
            if (!stats || stats.adherence === null) return ''
            if (stats.adherence < RISK_THRESHOLD) return 'risk'
            if (stats.adherence >= GOOD_THRESHOLD) return 'ok'
            return ''
        },
    },
    watch: {
        coachId() {
            const vm = this
            if (vm.page === 0) vm.loadStudents()
            else vm.page = 0
        },
        page() {
            this.loadStudents()
        },
    },
}
</script>

<style scoped>
.table-count {
    font-size: 11px;
    color: var(--v-txtMuted-base);
    margin-bottom: 8px;
}

.table-footer {
    display: flex;
    justify-content: flex-end;
    padding: 10px 4px 2px;
}

.risk {
    color: var(--v-error-base);
    font-weight: 600;
}

.ok {
    color: #009966;
}
</style>
